/**
 * Auth State Persistence Utility
 * Caches authentication state for offline access and faster app startup
 */

import { UserProfile } from '../types';
import { logCacheEvent } from './authLogger';
import { logger } from './logger';

const AUTH_CACHE_KEY = 'auth_cached_user';
const AUTH_CACHE_TIMESTAMP_KEY = 'auth_cache_timestamp';
const AUTH_SESSION_ID_KEY = 'auth_session_id';

// Cache expiry time: 24 hours
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000;

// Shorter expiry for offline mode: 7 days
const OFFLINE_CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

export interface CachedAuthState {
  user: UserProfile;
  cachedAt: number;
  expiresAt: number;
  isOfflineOnly?: boolean;
}

/**
 * Check if we're currently offline
 */
export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && !navigator.onLine;
}

/**
 * Cache the authenticated user state
 */
export function cacheAuthState(user: UserProfile): void {
  try {
    const now = Date.now();
    const cacheData: CachedAuthState = {
      user,
      cachedAt: now,
      expiresAt: now + CACHE_EXPIRY_MS,
    };
    
    localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(cacheData));
    localStorage.setItem(AUTH_CACHE_TIMESTAMP_KEY, now.toString());
    
    logCacheEvent('stored', { userId: user.sub, cachedAt: now });
    logger.debug('AuthPersistence', `Cached auth state for user: ${user.sub}`);
  } catch (error) {
    logger.error('AuthPersistence', 'Failed to cache auth state:', error);
  }
}

/**
 * Get cached authentication state
 * Returns null if cache is expired or doesn't exist
 */
export function getCachedAuthState(): CachedAuthState | null {
  try {
    const cached = localStorage.getItem(AUTH_CACHE_KEY);
    if (!cached) {
      logCacheEvent('miss');
      return null;
    }
    
    const cacheData: CachedAuthState = JSON.parse(cached);
    const now = Date.now();
    
    // Determine expiry based on online status
    const effectiveExpiry = isOffline() 
      ? cacheData.cachedAt + OFFLINE_CACHE_EXPIRY_MS 
      : cacheData.expiresAt;
    
    if (now > effectiveExpiry) {
      logCacheEvent('expired', { 
        userId: cacheData.user?.sub, 
        cachedAt: cacheData.cachedAt,
        expiredAt: effectiveExpiry,
      });
      clearCachedAuthState();
      return null;
    }
    
    logCacheEvent('hit', { 
      userId: cacheData.user?.sub,
      cachedAt: cacheData.cachedAt,
      isOffline: isOffline(),
    });
    
    return {
      ...cacheData,
      isOfflineOnly: isOffline(),
    };
  } catch (error) {
    logger.error('AuthPersistence', 'Failed to get cached auth state:', error);
    return null;
  }
}

/**
 * Clear cached authentication state
 */
export function clearCachedAuthState(): void {
  try {
    const cached = localStorage.getItem(AUTH_CACHE_KEY);
    let userId: string | undefined;
    
    if (cached) {
      try {
        const cacheData: CachedAuthState = JSON.parse(cached);
        userId = cacheData.user?.sub;
      } catch {}
    }
    
    localStorage.removeItem(AUTH_CACHE_KEY);
    localStorage.removeItem(AUTH_CACHE_TIMESTAMP_KEY);
    
    logCacheEvent('cleared', { userId });
    logger.debug('AuthPersistence', 'Cleared cached auth state');
  } catch (error) {
    logger.error('AuthPersistence', 'Failed to clear cached auth state:', error);
  }
}

/**
 * Check if cached auth state exists and is valid
 */
export function hasCachedAuthState(): boolean {
  return getCachedAuthState() !== null;
}

/**
 * Get the cached user directly (convenience method)
 */
export function getCachedUser(): UserProfile | null {
  const state = getCachedAuthState();
  return state?.user || null;
}

/**
 * Update specific fields in the cached user
 */
export function updateCachedUser(updates: Partial<UserProfile>): void {
  const state = getCachedAuthState();
  if (state?.user) {
    const updatedUser = { ...state.user, ...updates };
    cacheAuthState(updatedUser);
  }
}

/**
 * Get cache age in milliseconds
 */
export function getCacheAge(): number | null {
  const timestampStr = localStorage.getItem(AUTH_CACHE_TIMESTAMP_KEY);
  if (!timestampStr) return null;
  
  const timestamp = parseInt(timestampStr, 10);
  if (isNaN(timestamp)) return null;
  
  return Date.now() - timestamp;
}

/**
 * Check if cache is stale (older than half the expiry time)
 * Useful for proactive refresh
 */
export function isCacheStale(): boolean {
  const age = getCacheAge();
  if (age === null) return true;
  return age > CACHE_EXPIRY_MS / 2;
}

// Session ID management
export function getStoredSessionId(): string | null {
  return localStorage.getItem(AUTH_SESSION_ID_KEY);
}

export function storeSessionId(sessionId: string): void {
  localStorage.setItem(AUTH_SESSION_ID_KEY, sessionId);
}

export function clearSessionId(): void {
  localStorage.removeItem(AUTH_SESSION_ID_KEY);
}

// Export all functions
export const authPersistence = {
  cacheAuthState,
  getCachedAuthState,
  clearCachedAuthState,
  hasCachedAuthState,
  getCachedUser,
  updateCachedUser,
  getCacheAge,
  isCacheStale,
  isOffline,
  getStoredSessionId,
  storeSessionId,
  clearSessionId,
};

export default authPersistence;
